/**
 * @fileoverview DEFERRED locale bundles for `createAppI18n` — the factory-level
 * capability that stops every user downloading every language.
 *
 * ## The cost this removes
 *
 * `createAppI18n({ resources: { en, es } })` registers BOTH catalogs at boot, so both
 * are statically reachable from the entry and both land in the first-paint payload.
 * Measured on palm-web (2026-08-14): `@puffin-core/palm-shared`'s two catalogs render
 * to 43,626 B (en) + 49,886 B (es) of JSON inside `vendor-core-app`. An
 * English-speaking associate downloads the Spanish catalog and never renders one
 * character of it; a Spanish-speaking one downloads the English catalog for the same
 * nothing. The two are mutually exclusive by construction — a session resolves exactly
 * ONE language — so this is not a trade-off between size and capability.
 *
 * ## Why the capability lives in the FACTORY and not in an app
 *
 * `createAppI18n` is the canonical boot path for every web app (I18N-DEDUPE-001
 * Doctrine #1 — it is the sole sanctioned `i18next.createInstance()` site). A per-app
 * `import()` dance would be ten divergent hand-rolled implementations of the same
 * sequencing problem, nine of which would never get written. It also could not be
 * correct outside the factory: the resolved language is computed INSIDE
 * `createAppI18n` (`getInitialLanguage`: SSR seed → JWT claim → storage → navigator →
 * default), so anything deciding what to fetch before the factory runs is guessing.
 *
 * ## The three properties that make this safe
 *
 * 1. **OPT-IN.** The `lazyResources` option is absent at every existing call site
 *    (including the ~1,100-suite `pos-web` test harness), and every code path added
 *    here is gated on it being present. An app that does not pass it gets the byte-for-
 *    byte behaviour it had before — the instance's `changeLanguage` is not even
 *    decorated.
 * 2. **The RESOLVED language is loaded eagerly, and boot waits for it.**
 *    `createAppI18n` returns `resourcesReady`; the app's entry point awaits it before
 *    the first `createRoot().render()`. i18next returns the key itself for a missing
 *    leaf, so mounting before the active catalog is registered would render raw dotted
 *    keys at a user. Deferred locales are therefore only ever the INACTIVE ones.
 * 3. **A failed fetch falls back to a real language, never to dotted keys.**
 *    `ensure()` resolves `false` instead of rejecting; nothing switches; i18next keeps
 *    resolving through `fallbackLng` into the eagerly-supplied catalog. The failure is
 *    reported once and the attempt is DISCARDED, so the next call retries — a transient
 *    offline blip self-heals when the user picks the language again.
 *
 * ## Why the instance's `changeLanguage` is decorated
 *
 * `createAppI18n`'s docblock has said "apps NEVER call `i18n.changeLanguage` directly"
 * since I18N-LANG-001, and that has been a CONVENTION rather than a fact:
 * `useApplyServerLanguage` — this very package, the hook both preference bridges
 * delegate to — calls `i18n.changeLanguage(serverLanguage)` on the raw instance. Under
 * deferred locales an undecorated instance would switch to a language whose catalog was
 * never fetched, leaving the app's own copy resolving through the English fallback while
 * `<html lang>` announced Spanish — a half-translated page with a lying `lang`
 * attribute, which is the exact defect class AAS-I18N-002 exists to remove.
 *
 * Decorating the INSTANCE makes the invariant true by construction instead of by
 * convention: every path into a language switch loads the catalog first, whether it goes
 * through the factory's `changeLanguage` closure, through the preference bridges, or
 * through code not yet written.
 *
 * @module @puffin-core/i18n-bootstrap/lazyResources
 */

import { SUPPORTED_LANGUAGE_CODES } from './constants.js';

/**
 * The i18next namespace an app's OWN catalog occupies.
 *
 * Stated once here and imported by `createAppI18n`, because the deferred-load path and
 * the eager path must agree on it exactly: the loader asks `hasResourceBundle(code, ns)`
 * about the same namespace the eager path writes with `{ [ns]: bundle }`, and a
 * disagreement would make every deferred load look permanently un-loaded.
 *
 * @type {string}
 */
export const APP_TRANSLATION_NAMESPACE = 'translation';

/**
 * Human-readable description of a rejected loader result, for the error message.
 *
 * @param {*} value - Whatever the loader resolved to.
 * @returns {string} A short type description.
 */
function describeLoaderResult(value) {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'an array';
  if (typeof value !== 'object') return `a ${typeof value}`;
  return 'an empty object';
}

/**
 * Reduce a loader's resolved value to the translation bundle itself.
 *
 * 🔴 The documented loader contract is that it resolves to the BUNDLE
 * (`() => import('./es.json').then((m) => m.default)`), and the ecosystem call sites are
 * written that way. The module-namespace unwrap below is a second line of defence for a
 * bare `() => import('./es.json')`, and it is deliberately PROOF-BASED rather than
 * shape-based: it fires only when the value identifies itself as a module namespace
 * (`Symbol.toStringTag === 'Module'`, what Vite/Rollup/native ESM produce; or
 * `__esModule === true`, what a CJS interop helper produces).
 *
 * ⚠️ MEASURED, and the reason a shape heuristic was rejected: under this package's own
 * Jest transform an `await import('./x.json')` resolves to `{ default: {...}, ...spread }`
 * with **neither** marker set — babel's CommonJS interop copies the JSON's keys onto the
 * namespace and sets no flag. So "has a `default` key" cannot mean "is a module
 * namespace", and a heuristic tuned to the browser would have behaved DIFFERENTLY in the
 * test environment than in production. Under the proof-based rule both environments agree
 * for the documented call-site form, which is the only form the ecosystem uses.
 *
 * @param {*} loaded - The loader's resolved value.
 * @returns {Object|null} The bundle, or null when `loaded` cannot be one.
 */
function unwrapLazyBundle(loaded) {
  if (!loaded || typeof loaded !== 'object' || Array.isArray(loaded)) {
    return null;
  }
  const isModuleNamespace = loaded[Symbol.toStringTag] === 'Module' || loaded.__esModule === true;
  const nested = loaded.default;
  const bundle =
    isModuleNamespace && nested && typeof nested === 'object' && !Array.isArray(nested)
      ? nested
      : loaded;
  return Object.keys(bundle).length > 0 ? bundle : null;
}

/**
 * Validate the `lazyResources` option and freeze it into a loader map.
 *
 * Every failure here is a PROGRAMMER error discovered at boot, so every one throws by
 * name — the same posture as the removed-parameter tombstone in `createAppI18n`. A
 * RUNTIME failure (the fetch itself) is a different class and never throws; see
 * `createLazyResourceLoader`.
 *
 * @param {Object} params
 * @param {string} params.appName - For the diagnostics prefix.
 * @param {Object<string, Object>} params.resources - The EAGER bundles.
 * @param {*} params.lazyResources - The caller's raw option value.
 * @returns {Readonly<Object<string, () => Promise<*>>>|null} Frozen loader map, or null
 *   when the caller supplied none.
 */
export function normalizeLazyResources({ appName, resources, lazyResources }) {
  if (lazyResources === undefined || lazyResources === null) {
    return null;
  }
  if (typeof lazyResources !== 'object' || Array.isArray(lazyResources)) {
    throw new Error(
      `createAppI18n[${appName}]: lazyResources must be an object mapping a language code to a ` +
        'loader function, e.g. `{ es: () => import("./es.json").then((m) => m.default) }`',
    );
  }
  const codes = Object.keys(lazyResources);
  if (codes.length === 0) {
    return null;
  }
  for (const code of codes) {
    if (typeof lazyResources[code] !== 'function') {
      throw new Error(
        `createAppI18n[${appName}]: lazyResources.${code} must be a function returning a promise ` +
          'for the translation bundle',
      );
    }
    if (!SUPPORTED_LANGUAGE_CODES.includes(code)) {
      // Nothing can ever resolve to an unsupported code — `getInitialLanguage` and
      // `changeLanguage` both filter against this list — so such a loader is unreachable
      // code that would sit in the tree looking wired. Wire it or prune it.
      throw new Error(
        `createAppI18n[${appName}]: lazyResources.${code} names an unsupported language; no code ` +
          `path can ever resolve to it. Supported: ${SUPPORTED_LANGUAGE_CODES.join(', ')}`,
      );
    }
    if (Object.hasOwn(resources, code)) {
      throw new Error(
        `createAppI18n[${appName}]: "${code}" is in BOTH resources and lazyResources. A locale is ` +
          'either shipped in the first-paint payload or deferred — supplying both makes the ' +
          'deferred loader dead weight that never runs.',
      );
    }
  }
  return Object.freeze({ ...lazyResources });
}

/**
 * Build the on-demand catalog loader for one i18n instance.
 *
 * @param {Object} params
 * @param {string} params.appName - For the diagnostics prefix.
 * @param {import('i18next').i18n} params.i18n - The initialized instance.
 * @param {Readonly<Object<string, () => Promise<*>>>|null} params.loaders - Result of
 *   `normalizeLazyResources`.
 * @returns {{has: (code: *) => boolean, ensure: (code: *) => Promise<boolean>}}
 *   `has(code)` — is this code deferred? `ensure(code)` — resolves TRUE once the code's
 *   catalog is registered (or was never deferred), FALSE when the fetch failed. Never
 *   rejects.
 */
export function createLazyResourceLoader({ appName, i18n, loaders }) {
  /** @type {Map<string, Promise<boolean>>} in-flight/settled attempts, keyed by code. */
  const attempts = new Map();

  /**
   * Is `code` a locale this instance defers?
   *
   * ⚠️ The `has` check must come FIRST everywhere, before any `hasResourceBundle` call.
   * i18next's `hasResourceBundle(undefined, ns)` throws from inside `getResource`, and
   * `i18n.changeLanguage()` with no argument is a legal call this decorator sees.
   *
   * @param {*} code - Language code (or anything, from an untyped caller).
   * @returns {boolean}
   */
  function has(code) {
    return loaders !== null && typeof code === 'string' && Object.hasOwn(loaders, code);
  }

  /**
   * Guarantee `code`'s catalog is registered before the caller acts on it.
   *
   * @param {*} code - Language code.
   * @returns {Promise<boolean>} TRUE when the catalog is present (or was never deferred).
   */
  function ensure(code) {
    if (!has(code) || i18n.hasResourceBundle(code, APP_TRANSLATION_NAMESPACE)) {
      return Promise.resolve(true);
    }
    const inFlight = attempts.get(code);
    if (inFlight) {
      return inFlight;
    }
    const attempt = Promise.resolve()
      .then(() => loaders[code]())
      .then((loaded) => {
        const bundle = unwrapLazyBundle(loaded);
        if (bundle === null) {
          throw new Error(
            `lazyResources.${code}() resolved to ${describeLoaderResult(loaded)}; expected the ` +
              'translation bundle object (did you forget `.then((m) => m.default)`?)',
          );
        }
        // `overwrite: false` — a bundle already present wins, exactly as the pseudo-locale
        // installer does. `deep: true` matches the eager path's whole-catalog registration.
        i18n.addResourceBundle(code, APP_TRANSLATION_NAMESPACE, bundle, true, false);
        return true;
      })
      .catch((error) => {
        // DISCARD the failed attempt so a later call retries: a language switch that failed
        // on a flaky connection must heal when the user picks the language again, rather
        // than being memoized as permanently broken for the life of the tab.
        attempts.delete(code);
        // Reported, not swallowed: this is the ONE user-visible degradation this module
        // can produce (copy stays in the fallback language), and silence would make it
        // indistinguishable from a missing translation. `console.error` is the package's
        // sanctioned channel — it has no logger dependency, deliberately, so that a
        // foundational module never reaches up into a UI-layer one
        // (I18N-BOOTSTRAP-DEPCYCLE-001).
        console.error(
          `createAppI18n[${appName}]: deferred locale "${code}" failed to load — staying in ` +
            `"${i18n.resolvedLanguage}". The UI is fully translated in the fallback language; ` +
            'nothing renders as a raw key.',
          error,
        );
        return false;
      });
    attempts.set(code, attempt);
    return attempt;
  }

  return { has, ensure };
}

/**
 * Decorate the instance's own `changeLanguage` so EVERY caller — including ones that
 * bypass the factory's returned helper — fetches a deferred catalog before switching.
 *
 * Installed only when the app opted into `lazyResources`, so a non-adopting app's
 * instance keeps the untouched prototype method.
 *
 * @param {import('i18next').i18n} i18n - The initialized instance.
 * @param {{ensure: (code: *) => Promise<boolean>}} loader - From `createLazyResourceLoader`.
 * @returns {void}
 */
export function installLazyResourceChangeLanguage(i18n, loader) {
  const nativeChangeLanguage = i18n.changeLanguage.bind(i18n);
  i18n.changeLanguage = async function changeLanguageWithDeferredResources(code, callback) {
    if (await loader.ensure(code)) {
      return nativeChangeLanguage(code, callback);
    }
    // The switch did NOT happen. i18next's contract resolves `changeLanguage` with the
    // fixed translator for the language now in force, which is still the previous one.
    if (typeof callback === 'function') {
      callback(null, i18n.t);
    }
    return i18n.t;
  };
}
